import React, { useEffect, useState } from 'react';
import { Search, Filter, Eye, Download, RefreshCw } from 'lucide-react';
import { JobStatus, HistoryResponse, GenerationResponse } from '../types';
import api from '../services/api';
import { useProject } from '../context/ProjectContext';

import { useNavigate } from 'react-router-dom';

const History: React.FC = () => {
  const navigate = useNavigate();

  const onViewResult = (result: GenerationResponse) => {
    if (currentProject?.value) {
      navigate(`/projects/${currentProject.value}/results`, { state: { result } });
    } else {
      // Fallback if no project selected
      alert('Please select a project first');
    }
  };
  const { currentProject } = useProject();
  const [history, setHistory] = useState<HistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [loadingJob, setLoadingJob] = useState<string | null>(null);

  useEffect(() => {
    loadHistory();
  }, [page, statusFilter, currentProject]);

  const loadHistory = async () => {
    setIsLoading(true);
    try {
      const data = await api.getHistory({
        huId: searchTerm || undefined,
        status: statusFilter as JobStatus || undefined,
        page,
        pageSize: 10,
        projectId: currentProject?.value
      });
      setHistory(data);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = async (jobId: string) => {
    if (!onViewResult) return;

    setLoadingJob(jobId);
    try {
      const job = await api.getJob(jobId);
      onViewResult(job);
    } catch (error) {
      console.error('Failed to load job details:', error);
      alert('Error loading job details');
    } finally {
      setLoadingJob(null);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadHistory();
  };

  const getStatusBadge = (status: JobStatus) => {
    const styles = {
      [JobStatus.SUCCESS]: 'bg-green-500/10 text-green-400',
      [JobStatus.FAILED]: 'bg-red-500/10 text-red-400',
      [JobStatus.PROCESSING]: 'bg-blue-500/10 text-blue-400',
      [JobStatus.PENDING]: 'bg-yellow-500/10 text-yellow-400',
    };
    return styles[status] || 'bg-slate-500/10 text-slate-400';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">Generation History</h2>
        <p className="text-slate-400">Review past test case generation logs and outcomes.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="e.g., HU-4021"
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-primary"
          />
        </form>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white"
        >
          <option value="">All Statuses</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="processing">Processing</option>
        </select>

        <button
          onClick={() => { setPage(1); loadHistory(); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-blue-600 text-white rounded-lg transition-colors"
        >
          <Filter className="w-4 h-4" />
          Apply Filters
        </button>

        <button
          onClick={loadHistory}
          className="p-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
        >
          <RefreshCw className="w-5 h-5 text-slate-400" />
        </button>
      </div>

      {/* Table */}
      <div className="bg-surface-dark border border-slate-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-800/50">
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Job ID</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">HU ID</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Module</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Date & Time</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                </td>
              </tr>
            ) : history?.items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-slate-400">
                  No results found
                </td>
              </tr>
            ) : (
              history?.items.map((item) => (
                <tr key={item.job_id} className="hover:bg-slate-800/30 transition-colors">
                  <td className="px-6 py-4 text-sm font-mono text-slate-300">#{item.job_id}</td>
                  <td className="px-6 py-4 text-sm text-white font-medium">{item.hu_id}</td>
                  <td className="px-6 py-4 text-sm text-slate-400">{item.test_type}</td>
                  <td className="px-6 py-4 text-sm text-slate-400">{formatDate(item.created_at)}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(item.status)}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleView(item.job_id)}
                        disabled={loadingJob === item.job_id}
                        className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                        title="View"
                      >
                        {loadingJob === item.job_id ? (
                          <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Eye className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                      <button
                        className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors"
                        title="Download Excel"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const blob = await api.downloadExcel(item.job_id);
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `casos_prueba_${item.hu_id}.xlsx`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            window.URL.revokeObjectURL(url);
                          } catch (err) {
                            console.error('Download failed', err);
                            alert('Error downloading file');
                          }
                        }}
                      >
                        <Download className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {history && history.total_pages > 1 && (
          <div className="px-6 py-4 bg-slate-800/30 border-t border-slate-800 flex items-center justify-between">
            <p className="text-sm text-slate-400">
              Showing {((page - 1) * 10) + 1}-{Math.min(page * 10, history.total)} of {history.total} results
            </p>
            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(history.total_pages, 5) }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${p === page
                    ? 'bg-primary text-white'
                    : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
